import Alert from '@risk-smart/themed-cloudscape-components/alert';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './RelationalClickthroughOverlay.module.scss';

// RSP-5383 — relational data source widgets cannot click through to a single
// register, so their click-through is disabled. When the user interacts with an
// element inside such a widget, surface a high-contrast banner explaining why
// nothing navigated, instead of the (too subtle) RSP-5304 info popover.
const AUTO_DISMISS_MS = 5000;

// Elements we treat as "interactive" for the purposes of showing the banner.
// Charts render as inline `<svg>`; tables expose rows/cells; KPI/number widgets
// render clickable buttons/links. A click on pure container padding (no match)
// is ignored so the banner only fires on a real interaction (AC1).
// `[tabindex]:not([tabindex="-1"])` matches genuinely focusable elements while
// excluding wrappers that carry `tabindex="-1"` purely for programmatic focus.
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, svg, tr, td, th, [role="button"], [role="link"], [role="row"], [role="gridcell"], [tabindex]:not([tabindex="-1"])';

const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;

/**
 * Owns the banner visibility + the 5s auto-dismiss timer. `trigger()` shows the
 * banner and (re)starts the timer, so a repeat interaction before dismissal
 * resets the countdown (AC2/AC3). State is component-local, giving each widget
 * an independent banner (AC7). The timer is cleared on unmount.
 */
const useAutoDismissBanner = (timeoutMs: number = AUTO_DISMISS_MS) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const trigger = useCallback(() => {
    clear();
    setVisible(true);
    timerRef.current = setTimeout(() => {
      setVisible(false);
      timerRef.current = null;
    }, timeoutMs);
  }, [clear, timeoutMs]);

  useEffect(() => clear, [clear]);

  return { visible, trigger };
};

type Props = {
  /** True when the wrapped widget has click-through disabled (relational). */
  disabled: boolean;
  children: ReactNode;
};

export const RelationalClickthroughOverlay = ({
  disabled,
  children,
}: Props) => {
  const { t } = useTranslation();
  const { visible, trigger } = useAutoDismissBanner();

  const handleClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (isInteractiveTarget(event.target)) {
        trigger();
      }
    },
    [trigger]
  );

  const handleKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (
        (event.key === 'Enter' || event.key === ' ') &&
        isInteractiveTarget(event.target)
      ) {
        trigger();
      }
    },
    [trigger]
  );

  // `[&>span]:!contents [&>span>span]:!contents` neutralises the inline-block
  // `<span>` trigger wrappers Cloudscape's `Popover` injects: non-relational
  // popover-backed widgets (e.g. KPI / ValueWidget when `noClickthroughMessageContent`
  // is set and `onClick` is undefined) still render that leaf popover, and without
  // this guard the chart would be width-capped at the spans' intrinsic content
  // width. This preserves the layout guarantee the old `UniversalChart` wrapper
  // provided before the overlay replaced it.
  const layoutGuard = '[&>span]:!contents [&>span>span]:!contents';

  // Non-relational widgets are completely unaffected — render the chart as-is so
  // their click-through behaviour is unchanged (AC6).
  if (!disabled) {
    return <div className={`size-full ${layoutGuard}`}>{children}</div>;
  }

  return (
    // Capture-phase handlers run before the leaf chart/table handlers and don't
    // depend on threading a callback down through every Gigawidget variant.
    <div
      className={`relative size-full ${layoutGuard}`}
      onClickCapture={handleClickCapture}
      onKeyDownCapture={handleKeyDownCapture}
    >
      {children}
      {visible && (
        // Absolute overlay anchored to the widget's top — overlays content
        // without pushing it (AC8) and stays put when inner content scrolls
        // (AC9). `pointer-events-none` lets repeat clicks fall through to the
        // chart so the timer still resets (AC3). `role="alert"` +
        // `aria-live="assertive"` announce the message without moving focus
        // (AC10). `print:hidden` keeps this transient state out of dashboard
        // exports, matching the dashboard's other ephemeral UI.
        <div
          role={'alert'}
          aria-live={'assertive'}
          className={`pointer-events-none absolute inset-x-0 top-0 z-10 p-1 print:hidden ${styles.banner}`}
        >
          <Alert type={'error'}>
            {t('dashboard.relational_disabled_alert')}
          </Alert>
        </div>
      )}
    </div>
  );
};
